import { ComponentType, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Stack, SvgIconProps, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface DetailHeroHeaderProps {
  icon: ComponentType<SvgIconProps>;
  color?: string;
  title: ReactNode;
  chips?: ReactNode;
  actions?: ReactNode;
  backLabel: string;
  backTo: string;
  /** Extra content rendered below the title/chips row, e.g. a status selector or action buttons. */
  children?: ReactNode;
}

/** Shared hero header for entity detail pages: back link + icon avatar + title/chips + actions. */
export const DetailHeroHeader = ({
  icon: Icon,
  color = '#5B2DA3',
  title,
  chips,
  actions,
  backLabel,
  backTo,
  children,
}: DetailHeroHeaderProps) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backTo)} sx={{ mb: 2 }}>
        {backLabel}
      </Button>

      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${color}1a`,
                color,
                flexShrink: 0,
              }}
            >
              <Icon fontSize="medium" />
            </Box>
            <Box>
              <Typography variant="h5">{title}</Typography>
              {chips && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {chips}
                </Stack>
              )}
            </Box>
          </Stack>
          {actions}
        </Stack>
        {children && <Box sx={{ mt: 2 }}>{children}</Box>}
      </Paper>
    </Box>
  );
};
